import React, {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import Toggle from "react-toggle";
import ZeusButton from "../../components/Button";
import ImageUploadCreator from "../../components/Forms/components/ImageUploadCreator";
import SelectCreator from "../../components/Forms/components/SelectCreator";
import ProtectedContent from "../../components/protectedContent";
import TimezoneList from "../../constants/timezones.json";
import {saveOrganizationImages} from "../../redux/Actions/root.actions";
import {saveUserSettings} from "../../redux/Actions/users.action";
import {getOrganizationImages} from "../../redux/Selectors/root.selector";
import {getCustomerUserProfile} from "../../redux/Selectors/users.selector";
import "./settings.scss";

const Settings = () => {
  const dispatch = useDispatch();
  const profileSettings = useSelector(getCustomerUserProfile);
  const [payload, setPayload] = useState({
    notify_email: profileSettings?.notify_email,
    timezone: profileSettings?.timezone,
  });
  const {backgroundImg, background_type, logoImg} = useSelector(getOrganizationImages);

  const [brandingFiles, setBrandingFiles] = useState({
    background_image: null,
    logo: null,
  });

  const [removedImages, setRemovedImages] = useState({
    background_image: false,
    logo: false,
  });

  const onChangeHandler = ({target: {value, name, type, checked}}) => {
    setPayload((prev) => ({...prev, [name]: type === "checkbox" ? checked : value}));
  };

  const onBrandingChange = ({target}) => {
    setRemovedImages((prev) => ({...prev, [target.name]: target.file === null}));
    setBrandingFiles((prev) => ({...prev, [target.name]: target.file ?? null}));
  };

  const uploadBranding = () => {
    const {background_image, logo} = brandingFiles;
    const nothingNew = !background_image && !logo;
    const nothingRemoved = !removedImages.background_image && !removedImages.logo;
    if (nothingNew && nothingRemoved)
      return toast.error("Please select a file to upload");

    const formData = new FormData();
    if (logo) formData.append("logo", logo);
    else if (removedImages.logo) formData.append("logo", "");
    if (background_image) formData.append("background", background_image);
    else if (removedImages.background_image) formData.append("background", "");

    dispatch(saveOrganizationImages(formData));
    setBrandingFiles({background_image: null, logo: null});
    setRemovedImages({background_image: false, logo: false});
  };

  return (
    <div className="settings-container">
      <div className="settings-cards-wrapper">
        {/* ── General Settings card ── */}
        <div className="settings-card">
          <div className="section-label">
            <h3>General Settings</h3>
          </div>
          <div className="settings-card-body">
            {/* Email notifications row */}
            <div className="form-row">
              <div className="label-column">
                <h4 className="row-title">Email Notifications</h4>
                <p className="row-description">
                  Receive email updates about your account activity
                </p>
              </div>
              <div className="control-column">
                <div className="toggle-wrapper">
                  <div className="toggle-content">
                    <p className="toggle-title">
                      {payload?.notify_email ? "Enabled" : "Disabled"}
                    </p>
                    <p className="toggle-subtitle">
                      {payload?.notify_email
                        ? "You will receive email notifications"
                        : "Email notifications are turned off"}
                    </p>
                  </div>
                  <Toggle
                    checked={!!payload?.notify_email}
                    name="notify_email"
                    onChange={onChangeHandler}
                  />
                </div>
              </div>
            </div>

            {/* Timezone row */}
            <div className="form-row">
              <div className="label-column">
                <h4 className="row-title">Timezone</h4>
                <p className="row-description">
                  Select your local timezone for accurate time display
                </p>
              </div>
              <div className="control-column">
                <SelectCreator
                  isSearchable
                  large
                  name="timezone"
                  onChange={onChangeHandler}
                  options={TimezoneList}
                  value={payload?.timezone}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>
          </div>

          <div className="settings-card-footer">
            <ZeusButton onClick={() => dispatch(saveUserSettings(payload))}>
              Save Changes
            </ZeusButton>
          </div>
        </div>

        {/* ── Branding card ── */}
        <ProtectedContent perms={["add_organizationbranding"]}>
          <div className="settings-card">
            <div className="section-label">
              <h3>Branding</h3>
            </div>

            <div className="settings-card-body">
              {/* Logo row */}
              <div className="form-row">
                <div className="label-column">
                  <h4 className="row-title">Logo</h4>
                  <p className="row-description">
                    128×128 to 1024×1024 px
                    <br />
                    Maximum file size: 1 MB
                    <br />
                    Square or near-square preferred
                  </p>
                </div>
                <div className="control-column">
                  <ImageUploadCreator
                    inline
                    name="logo"
                    file={brandingFiles.logo}
                    value={logoImg ?? null}
                    onChange={onBrandingChange}
                  />
                </div>
              </div>

              {/* Background image row */}
              <div className="form-row">
                <div className="label-column">
                  <h4 className="row-title">Background Image</h4>
                  <p className="row-description">
                    1280×720 to 3840×2160 px
                    <br />
                    Maximum file size: Images (8 MB), Videos (20 MB)
                    <br />
                    16:9 aspect ratio recommended
                  </p>
                </div>
                <div className="control-column">
                  <ImageUploadCreator
                    inline
                    accept="image/*,video/*"
                    name="background_image"
                    file={brandingFiles.background_image}
                    fileType={background_type}
                    value={backgroundImg ?? null}
                    onChange={onBrandingChange}
                  />
                </div>
              </div>
            </div>

            <div className="settings-card-footer">
              <ZeusButton onClick={uploadBranding}>Save Changes</ZeusButton>
            </div>
          </div>
        </ProtectedContent>
      </div>
    </div>
  );
};

export default Settings;
